import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { CmUiDialogService } from '@cm/ui-modules';
import { TranslatePipe } from '@ngx-translate/core';
import { isSystemUpdateVideo, systemUpdateMediaSrc } from '@modules/system-updates/system-updates.model';
import {
  SystemUpdateImageDialogComponent,
  SystemUpdateImageDialogData,
} from '../system-update-image-dialog/system-update-image-dialog.component';

/**
 * A system update's media: an image opens in a lightbox, a video plays inline with the native
 * controls (which have their own fullscreen). `mediaUrl` / `thumbnailUrl` are the media-asset file
 * names the API returns — this component prefixes `/mediaassets/`. Shared by the panel, the archive
 * viewer and the editor preview. A host may cap the image height with `--su-media-image-max-height`
 * (default 260px).
 */
@Component({
  selector: 'app-system-update-media',
  standalone: true,
  imports: [TranslatePipe],   // no SHARED_IMPORTS — see system-update-body.component.ts
  templateUrl: './system-update-media.component.html',
  styleUrls: ['./system-update-media.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SystemUpdateMediaComponent {
  private readonly dialog = inject(CmUiDialogService);

  readonly mediaUrl = input<string | null | undefined>(null);
  readonly thumbnailUrl = input<string | null | undefined>(null);
  /** `video` / `image` as the API reports it; the file extension decides when absent. */
  readonly mediaType = input<string | null | undefined>(null);
  /** Alt text and lightbox title. */
  readonly title = input<string>('');
  /**
   * An image opens in the lightbox on click. Off where the image is already shown as large as the
   * screen allows (the "What's new" viewer) — a lightbox there would only show it smaller.
   */
  readonly zoomable = input(true);

  protected readonly src = computed(() => systemUpdateMediaSrc(this.mediaUrl()));
  protected readonly poster = computed(() => systemUpdateMediaSrc(this.thumbnailUrl()));
  protected readonly isVideo = computed(() => isSystemUpdateVideo(this.mediaUrl(), this.mediaType()));

  protected expand(): void {
    const src = this.src();
    if (!src || !this.zoomable()) {
      return;
    }
    this.dialog.open<SystemUpdateImageDialogComponent, SystemUpdateImageDialogData>(
      SystemUpdateImageDialogComponent,
      { width: '960px', data: { src, title: this.title() } },
    );
  }
}
